<script setup>
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import MacrosTableRow from './MacrosTableRow.vue';
import MacroLoadError from '../../conversation/Macros/MacroLoadError.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import { BaseTable } from 'dashboard/components-next/table';
import { useAdmin } from 'dashboard/composables/useAdmin';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeleteConfirmationPopup = ref(false);
const selectedMacro = ref({});
const searchQuery = ref('');

const records = computed(() => getters['macros/getMacros'].value);
const uiFlags = computed(() => getters['macros/getUIFlags'].value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name']);
});

const canManageMacro = macro => isAdmin.value || macro.visibility !== 'global';

const deleteMessage = computed(() => ` ${selectedMacro.value.name}?`);

onMounted(() => {
  store.dispatch('macros/get');
});

const deleteMacro = async id => {
  try {
    await store.dispatch('macros/delete', id);
    useAlert(t('MACROS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('MACROS.DELETE.API.ERROR_MESSAGE'));
  }
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedMacro.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = () => {
  closeDeletePopup();
  deleteMacro(selectedMacro.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('MACROS.LIST.TABLE_HEADER.NAME'),
    t('MACROS.LIST.TABLE_HEADER.CREATED BY'),
    t('MACROS.LIST.TABLE_HEADER.LAST_UPDATED_BY'),
    t('MACROS.LIST.TABLE_HEADER.VISIBILITY'),
    t('MACROS.LIST.TABLE_HEADER.ACTIONS'),
  ];
});
</script>

<template>
  <SettingsLayout
    :no-records-message="$t('MACROS.LIST.404')"
    :no-records-found="!uiFlags.hasFetchError && !records.length"
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('MACROS.LOADING')"
    feature-name="macros"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('MACROS.HEADER')"
        :description="$t('MACROS.DESCRIPTION')"
        :link-text="$t('MACROS.LEARN_MORE')"
        :search-placeholder="$t('MACROS.SEARCH_PLACEHOLDER')"
        feature-name="macros"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('MACROS.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link :to="{ name: 'macros_new' }">
            <Button :label="$t('MACROS.HEADER_BTN_TXT')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <MacroLoadError v-if="uiFlags.hasFetchError" />
      <template v-else>
        <div class="flex flex-col gap-3 sm:hidden">
          <article
            v-for="macro in filteredRecords"
            :key="`mobile-${macro.id}`"
            :data-macro-id="macro.id"
            class="rounded-xl border border-n-weak bg-n-solid-2 p-3"
          >
            <div class="flex min-w-0 items-start justify-between gap-3">
              <p
                class="m-0 min-w-0 flex-1 break-words text-heading-3 text-n-slate-12"
              >
                {{ macro.name }}
              </p>
              <span class="shrink-0 text-body-main text-n-slate-11">
                {{
                  macro.visibility === 'global'
                    ? $t('MACROS.EDITOR.VISIBILITY.GLOBAL.LABEL')
                    : $t('MACROS.EDITOR.VISIBILITY.PERSONAL.LABEL')
                }}
              </span>
            </div>
            <dl
              class="mt-3 grid grid-cols-1 gap-1 text-body-main text-n-slate-11"
            >
              <div class="flex min-w-0 justify-between gap-3">
                <dt class="shrink-0">{{ tableHeaders[1] }}</dt>
                <dd class="m-0 min-w-0 break-words text-end text-n-slate-12">
                  {{
                    macro.created_by?.available_name ||
                    macro.created_by?.email ||
                    '--'
                  }}
                </dd>
              </div>
              <div class="flex min-w-0 justify-between gap-3">
                <dt class="shrink-0">{{ tableHeaders[2] }}</dt>
                <dd class="m-0 min-w-0 break-words text-end text-n-slate-12">
                  {{
                    macro.updated_by?.available_name ||
                    macro.updated_by?.email ||
                    '--'
                  }}
                </dd>
              </div>
            </dl>
            <div class="mt-3 flex items-center justify-end gap-2">
              <router-link
                :to="{ name: 'macros_edit', params: { macroId: macro.id } }"
                class="inline-flex min-h-11 items-center rounded-md px-3 text-body-main text-n-slate-12 hover:bg-n-alpha-2"
                :aria-label="
                  canManageMacro(macro)
                    ? $t('MACROS.EDIT.TOOLTIP')
                    : $t('MACROS.VIEW.TOOLTIP')
                "
              >
                {{
                  canManageMacro(macro)
                    ? $t('MACROS.EDIT.TOOLTIP')
                    : $t('MACROS.VIEW.TOOLTIP')
                }}
              </router-link>
              <Button
                v-if="canManageMacro(macro)"
                :label="$t('MACROS.DELETE.TOOLTIP')"
                slate
                sm
                class="min-h-11 hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                @click="openDeletePopup(macro)"
              />
            </div>
          </article>
          <p
            v-if="!filteredRecords.length"
            class="py-10 text-center text-body-main text-n-slate-11"
          >
            {{ searchQuery ? $t('MACROS.NO_RESULTS') : $t('MACROS.LIST.404') }}
          </p>
        </div>
        <div class="hidden sm:block">
          <BaseTable
            :headers="tableHeaders"
            :items="filteredRecords"
            :no-data-message="
              searchQuery ? $t('MACROS.NO_RESULTS') : $t('MACROS.LIST.404')
            "
          >
            <template #row="{ items }">
              <MacrosTableRow
                v-for="macro in items"
                :key="macro.id"
                :macro="macro"
                :can-manage-public-macros="isAdmin"
                @delete="openDeletePopup(macro)"
              />
            </template>
          </BaseTable>
        </div>
      </template>
      <woot-delete-modal
        v-model:show="showDeleteConfirmationPopup"
        :on-close="closeDeletePopup"
        :on-confirm="confirmDeletion"
        :title="$t('MACROS.DELETE.CONFIRM.TITLE')"
        :message="$t('MACROS.DELETE.CONFIRM.MESSAGE')"
        :message-value="deleteMessage"
        :confirm-text="$t('MACROS.DELETE.CONFIRM.YES')"
        :reject-text="$t('MACROS.DELETE.CONFIRM.NO')"
      />
    </template>
  </SettingsLayout>
</template>
